import React from 'react';
import { cn } from '@/lib/cn';
import { IconChevronRight } from '@repo/svgs';

export type HighlightType = 'neutral' | 'caution' | 'positive';
export type HighlightValueType = 'text' | 'icon';

export interface HighlightCardProps {
  /** The type determines the color scheme and semantic meaning */
  type?: HighlightType;
  /** Whether to display a text value or an icon */
  valueType?: HighlightValueType;
  /** The text or numeric value to display (only used when valueType is 'text') */
  value?: string | number;
  /** The label text describing what this highlight represents */
  label: string;
  /** Optional CSS class name */
  className?: string;
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Optional click handler */
  onClick?: () => void;
}

export function HighlightCard({
  type = 'neutral',
  valueType = 'text',
  value = '0',
  label,
  className,
  icon,
  onClick,
  ...props
}: HighlightCardProps) {
  // Value container styles based on type - matches Figma design
  const getValueContainerStyles = () => {
    const baseStyles = 'rounded-full shrink-0 px-3 py-1 flex items-center justify-center';

    switch (type) {
      case 'caution':
        return cn(baseStyles, 'bg-caution-low');
      case 'positive':
        return cn(baseStyles, 'bg-success-low');
      default: // neutral
        return cn(baseStyles, 'bg-secondary');
    }
  };

  // Value text styles based on type - matches Figma design
  const getValueTextStyles = () => {
    switch (type) {
      case 'caution':
        return 'text-caution text-body-1';
      case 'positive':
        return 'text-success text-body-1';
      default: // neutral
        return 'text-secondary text-body-1';
    }
  };

  return (
    <div
      className={cn(
        // Base container styles - matches Figma design
        'bg-primary border border-secondary rounded-2xl shadow',
        // Mobile: vertical stack, Desktop: horizontal
        'flex flex-col items-start gap-3 px-3 py-4',
        'md:flex-row md:items-center md:gap-4 md:px-4 md:py-6',
        // Interactive states
        'cursor-pointer hover:bg-secondary transition-colors',
        'basis-0 grow min-w-0',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {/* Value container - badge style */}
      <div className={cn(getValueContainerStyles(), 'md:self-auto')}>
        {valueType === 'icon' ? (
          icon
        ) : (
          <span className={getValueTextStyles()}>{value == 0 ? 'No' : value}</span>
        )}
      </div>

      {/* Key container - label and chevron */}
      <div className="flex items-center gap-1 flex-1 min-w-0 w-full">
        <span className="text-primary text-body-2 flex-1 min-w-0">{label}</span>
        <IconChevronRight className="w-3 h-3 icon-secondary shrink-0" />
      </div>
    </div>
  );
}
